import WebFont from 'webfontloader'
import { useEffect } from 'react'
import PropTypes from 'prop-types'

import styled from 'styled-components'
import { border, layout } from 'styled-system'
import { useMedia } from 'use-media'

import {
  Box,
  InputWrapper,
  inputIconWidth,
  inputStyles,
  media,
  mediaQueries,
  shouldForwardProp,
} from '@redriverpress/system'

import Bold from '@tiptap/extension-bold'
import BulletList from '@tiptap/extension-bullet-list'
import CharacterCount from '@tiptap/extension-character-count'
import Document from '@tiptap/extension-document'
import FontFamily from '@tiptap/extension-font-family'
import HardBreak from '@tiptap/extension-hard-break'
import History from '@tiptap/extension-history'
import Italic from '@tiptap/extension-italic'
import ListItem from '@tiptap/extension-list-item'
import OrderedList from '@tiptap/extension-ordered-list'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import Text from '@tiptap/extension-text'
import TextAlign from '@tiptap/extension-text-align'
import TextStyle from '@tiptap/extension-text-style'
import Underline from '@tiptap/extension-underline'
import { Color } from '@tiptap/extension-color'
import { EditorContent, useEditor } from '@tiptap/react'

import { FontSizeExtension } from './FontSizeCustomExtension'

import TextAlignment from './TextAlignmentButton'
import TextMarkingGroup from './MarkingGroup'
import ListItemsGroup from './ListGroup'
import FontFamilySelector from './FontFamilySelector'
import FontSizeOptions from './FontSizeSelector'
import ColorSelectorOptions from './ColorSelector'

export const richIconWidth = '0.8rem'

// Initial editor style
// According to tiptap documentation, all the editor component
// styles can be modified using the ProseMirror class.
const StyledEditor = styled(EditorContent)
  .withConfig({ shouldForwardProp })
  .attrs(props => ({
    borderTop: props.label
      ? '1.5rem solid transparent'
      : '0.5rem solid transparent',
    py: 3,
  }))`
    .ProseMirror {
      ${inputStyles}
      ${border}
      ${layout}

      font-size: ${props => props.theme.fontSizes[0]};
      > * + * {
        margin-top: 0.5em;
      }

      white-space: pre-wrap;
      line-height: ${props => props.theme.lineHeights.body};
      min-height: ${props => props.minHeight || props.theme.space[5]};
      padding-top: 0 !important;
      resize: vertical;
      background: ${props =>
        props.readOnly ? 'transparent' : props.theme.colors.white};

      outline: none;
      box-shadow: ${props => props.readOnly && 'none'};
      &:focus {
        box-shadow: ${props => props.readOnly && 'none'};
      }

      ul,
      ol {
        padding: 0 1rem;
        list-style-position: unset;
      }

      // Placeholder configuration
      p.is-editor-empty:first-child::after {
        color: ${props => props.theme.colors.gray300};
        content: '${props => props.placeholder}';
        float: left;
        height: 0;
        margin-top: -22px;
        white-space: pre-wrap;
        pointer-events: none;

        ${media.xl`
          margin-top: -25px;
        `}
    }
`

const StyledMenuBar = styled(Box).attrs({
  mb: 2,
  display: 'flex',
})`
  overflow-x: auto;
  -ms-overflow-style: none; /* Edge */
  scrollbar-height: none; /* Firefox */

  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari, and Opera */
  }
`

const MobileScrollOverlay = styled(Box).attrs({
  width: '45px',
  height: '40px',
  position: 'absolute',
})`
  background: linear-gradient(
    ${props => props.degree}deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(249, 249, 249, 1) 30%,
    rgba(249, 249, 249, 1) 100%
  );
`

const RichMenuBar = ({ editor }) => {
  const isMd = useMedia(mediaQueries.md)

  return (
    <Box display='flex' alignItems='center'>
      {!isMd && <MobileScrollOverlay left={0} degree='270' />}
      <StyledMenuBar>
        <FontFamilySelector editor={editor} />
        <FontSizeOptions editor={editor} />
        <ColorSelectorOptions editor={editor} />
        <TextMarkingGroup editor={editor} />
        <TextAlignment editor={editor} />
        <ListItemsGroup editor={editor} />
      </StyledMenuBar>
      {!isMd && <MobileScrollOverlay right={0} degree='90' />}
    </Box>
  )
}

RichMenuBar.propTypes = {
  editor: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object, PropTypes.bool])
  ).isRequired,
}

export default function RichTextarea({
  disabled,
  error,
  icon,
  label,
  rows,
  wrapperProps,
  onChange,
  savedContent,
  placeholder,
  maxLength,
  readOnly,
  ...props
}) {
  const editor = useEditor({
    extensions: [
      Bold,
      BulletList,
      CharacterCount.configure({
        limit: maxLength,
      }),
      Color,
      Document,
      FontFamily,
      FontSizeExtension,
      HardBreak,
      History,
      Italic,
      ListItem,
      OrderedList,
      Paragraph,
      Text,
      Placeholder,
      TextAlign.configure({
        types: ['paragraph'],
      }),
      TextStyle,
      Underline,
    ],
    injectCSS: false,
  })

  useEffect(() => {
    WebFont.load({
      google: {
        families: [
          'Comic Neue:400,400italic,700,700italic',
          'Noto Serif:400,400italic,700,700italic',
          'Open Sans:400,400italic,700,700italic',
        ],
      },
    })
  }, [])

  useEffect(() => {
    if (editor) {
      editor.on('update', () => {
        let value = editor.getHTML()
        if (editor.isEmpty) value = null
        onChange(value)
      })
    }

    return () => {
      editor && editor.off('update')
    }
  }, [editor])

  useEffect(() => {
    if (editor && !editor?.isFocused && !!savedContent) {
      editor.commands.setContent(savedContent)
    }

    if (editor && readOnly) {
      editor.setEditable(false)
      editor.commands.setContent(savedContent)
    }
  }, [savedContent, editor])

  return editor && readOnly ? (
    <StyledEditor editor={editor} {...props} readOnly />
  ) : (
    editor && (
      <>
        <RichMenuBar editor={editor} />
        <InputWrapper
          limitIconHeight
          icon={icon}
          label={label}
          error={error}
          {...wrapperProps}
        >
          <StyledEditor
            label={label}
            editor={editor}
            pl={icon ? inputIconWidth : 3}
            pr={3}
            pb={2}
            rows={rows}
            placeholder={placeholder}
            {...props}
          />
        </InputWrapper>
      </>
    )
  )
}

RichTextarea.defaultProps = {
  maxLength: null,
  readOnly: false,
  onChange: () => {},
}

RichTextarea.propTypes = {
  disabled: PropTypes.bool.isRequired,
  error: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  rows: PropTypes.number.isRequired,
  onChange: PropTypes.func,
  savedContent: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  maxLength: PropTypes.number,
  readOnly: PropTypes.bool,
}
